import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ApiMethod } from '../../apimethods/api-method';

export interface WorkflowDetail {
  stage_name?: string | null;
  sequence?: string | number | null;
  submitted_on?: string | null;
  name?: string | null;
  emailaddress?: string | null;
  snap_path?: string | null;
  article_workflow_status?: string | number | null;
  [key: string]: any;
}

export interface ArticleRecord {
  journalCode?: string | null;
  articleId?: string | null;
  proofLinkSent?: string | null;
  firstProofLink?: string | null;
  articleStatus?: string | null;
  workflowDetails?: WorkflowDetail[] | null;
  [key: string]: any;
}

interface SearchableArticle {
  data: ArticleRecord;
  searchableText: string;
}

@Component({
  selector: 'app-nedit-dashboard',
  templateUrl: './nedit-dashboard.component.html',
  styleUrls: ['./nedit-dashboard.component.css']
})
export class NeditDashboardComponent implements OnInit {

  articles: ArticleRecord[] = [];
  private indexedArticles: SearchableArticle[] = [];

  filteredArticles: ArticleRecord[] = [];

  searchText = '';
  loading = false;
  errorMessage = '';
  totalRecords = 0;

  pageSize = 10;
  currentPage = 1;

  readonly pageSizeOptions: number[] = [10, 25, 50, 100];

  constructor(private http: HttpClient) {}

  ngOnInit(): void {
    this.loadJsonData();
  }

  get totalPages(): number {
    return Math.max(
      1,
      Math.ceil(this.filteredArticles.length / this.pageSize)
    );
  }

  get pagedArticles(): ArticleRecord[] {
    const start = (this.currentPage - 1) * this.pageSize;
    return this.filteredArticles.slice(start, start + this.pageSize);
  }

  get firstRecordNumber(): number {
    return this.filteredArticles.length === 0
      ? 0
      : (this.currentPage - 1) * this.pageSize + 1;
  }

  get lastRecordNumber(): number {
    return Math.min(
      this.currentPage * this.pageSize,
      this.filteredArticles.length
    );
  }

  loadJsonData(): void {
    this.loading = true;
    this.errorMessage = '';

    const dataParam = {
      LoginId: localStorage.getItem('aLoginID') ?? '',
      PublisherId: localStorage.getItem('aPubID') ?? ''
    };

    // Uses your existing ApiMethod service and POST endpoint.
    ApiMethod.postMethod('dash/nEditData', dataParam).subscribe({
      next: (response: any) => {
        const records: ArticleRecord[] = Array.isArray(response)
          ? response
          : Array.isArray(response?.data)
            ? response.data
            : [];

        this.articles = records.map((article: ArticleRecord) => ({
          ...article,
          workflow_details: Array.isArray(article.workflow_details)
            ? article.workflow_details
            : []
        }));

        this.indexedArticles = this.articles.map(
          (article: ArticleRecord) => ({
            data: article,
            searchableText: this.flattenJson(article).toLowerCase()
          })
        );

        this.totalRecords = this.articles.length;
        this.applyFilter();
        this.loading = false;

        console.log('nEdit articles loaded:', this.totalRecords);
      },
      error: (error: any) => {
        console.error('nEdit API request failed:', error);

        this.errorMessage =
          error?.error?.message ||
          error?.message ||
          'Unable to load nEdit dashboard data.';

        this.loading = false;
      }
    });
  }

  private flattenJson(value: any): string {
    if (value === null || value === undefined) {
      return '';
    }

    if (Array.isArray(value)) {
      return value
        .map((item: any) => this.flattenJson(item))
        .join(' ');
    }

    if (typeof value === 'object') {
      return Object.keys(value)
        .map((key: string) => {
          // Include property names as well as their values in global search.
          return key + ' ' + this.flattenJson(value[key]);
        })
        .join(' ');
    }

    return String(value);
  }

  applyFilter(): void {
    const query = this.searchText.trim().toLowerCase();

    this.filteredArticles = query
      ? this.indexedArticles
          .filter((item: SearchableArticle) =>
            item.searchableText.includes(query)
          )
          .map((item: SearchableArticle) => item.data)
      : this.indexedArticles.map(
          (item: SearchableArticle) => item.data
        );

    this.currentPage = 1;
  }

  clearSearch(): void {
    this.searchText = '';
    this.applyFilter();
  }

  onPageSizeChange(value: string): void {
    const parsed = Number(value);

    if (this.pageSizeOptions.includes(parsed)) {
      this.pageSize = parsed;
      this.currentPage = 1;
    }
  }

  previousPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
    }
  }

  nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
    }
  }

  trackByArticle(
    index: number,
    article: ArticleRecord
  ): string | number {
    return article.articleId || index;
  }

  hasWorkflowFiles(article: ArticleRecord): boolean {
    return !!article.WorkflowDetails?.some(
      workflow => !!workflow.SnapPath
    );
  }

  refreshData(): void {
    if (this.loading) {
      return;
    }

    this.currentPage = 1;
    this.loadJsonData();
  }

}
